import { scoreBand, type ScoreBand } from '@/design/ScoreColors';
import type { AdoptionBreakdownRow } from './AdoptionTypes';

/** An adoption maturity band — the shared 0–100 scoring band (Leading → brand indigo, Established → periwinkle, Emerging → amber, Dormant → rose). */
export type AdoptionBand = ScoreBand;

/** Maturity band for an adoption score, from the shared {@link scoreBand} scale. */
export function adoptionBand(score: number): AdoptionBand {
    return scoreBand(score);
}

/** One segment of the adoption-level distribution: a maturity band (or Inactive) and its user count. */
export interface AdoptionLevelCount {
    name: string;
    color: string;
    count: number;
}

/**
 * Buckets breakdown rows into the four maturity bands plus a distinct Inactive
 * segment (directory identities with no activity). Ordered Leading → Dormant →
 * Inactive for a top-down distribution readout. Colors come from the design
 * tokens via {@link adoptionBand}; Inactive uses the dimmed-text token so it
 * reads apart from Dormant.
 */
export function adoptionLevelCounts(rows: AdoptionBreakdownRow[]): AdoptionLevelCount[] {
    const bandCounts = new Map<string, number>();
    let inactive = 0;
    for (const row of rows) {
        if (row.inactive) {
            inactive += 1;
            continue;
        }
        const name = adoptionBand(row.adoption).name;
        bandCounts.set(name, (bandCounts.get(name) ?? 0) + 1);
    }
    const bands = [adoptionBand(80), adoptionBand(60), adoptionBand(30), adoptionBand(0)];
    return [
        ...bands.map((band) => ({ name: band.name, color: band.color, count: bandCounts.get(band.name) ?? 0 })),
        { name: 'Inactive', color: 'var(--bm-color-text-dimmed)', count: inactive },
    ];
}
